/**
 * Store: Cart
 * ------------------------------------------------------------------------------
 * Global store for the Shopify cart.
 * - https://github.com/SheepNDW/vue3-pinia-shopping-cart/blob/master/src/stores/cartStore.js
 * - https://github.com/beautifulwhale/pinia-shopping-cart/blob/main/src/store/cart.ts
 *
 * @namespace useCartStore
 */
import { defineStore } from 'pinia'

import * as Cart from '~/api/cart'

import { storageNamespace } from '~/helpers/storage-namespaces'

/**
 * Export a cart store.
 */
export const useCartStore = defineStore({
  id: 'cart',

  state: () => {
    return {
      cartData: {
        attributes: {},
        cart_level_discount_applications: [],
        currency: 'GBP',
        item_count: 0,
        items: [],
        items_subtotal_price: 0,
        note: null,
        original_total_price: 0,
        request: {
          event: 'cart:initialise',
        },
        requires_shipping: false,
        token: null,
        total_discount: 0,
        total_price: 0,
        total_weight: 0,
      },
    }
  },

  actions: {

    /**
     * Add a given item to the cart.
     * - Should be used like any Promise-based function
     * - Takes the same parameters as standard Cart JS API:
     * - https://shopify.dev/api/ajax/reference/cart#post-locale-cart-add-js
     * @param {Object} payload - Cart payload data.
     * @param {Object} payload.data - Cart JS 'add.js' payload.
     * @param {Object} payload.meta - Custom object to pass data through, useful for
     * GA, Bloomreach, Attraqt event tracking etc.
     * @param {Boolean} payload.preventDrawer - Determines whether to show cart modal or not.
     * @return {Array|Object} - Cart JS 'add.js' response.
     */
    addItems(payload) {
      return new Promise((resolve, reject) => {
        let request = {}

        Cart.addItems(payload.data)
          .then((response) => {
            request = {
              data: payload.data,
              event: 'cart:added',
              meta: JSON.parse(JSON.stringify(payload.meta) || '{}'),
              preventDrawer: payload.preventDrawer || false,
            }

            /**
             * Add to cart has failed.
             * - Shopify returned a 200, but couldn't add to cart.
             * - Usually down to item quantity or variant ID errors.
             * - Fetch/Network requests occur within the `catch`.
             */
            if (response?.message) {
              request.event = 'cart:addFailed'
              request.error = response
            }

            this.handleCartUpdate({ request })

            if (request.event === 'cart:addFailed') {
              reject(response)
            }

            resolve(response)
          })
          .catch((error) => {
            mint.Log('fatal', 'Failed to add items to the cart', error, {
              request,
            })

            reject(error)
          })
      })
    },

    /**
     * Remove a given line item from the cart.
     * @param {Object} payload - Cart payload data.
     * @param {String} payload.key - Cart line item key.
     * @param {Object} payload.meta - Custom object to pass data through, useful for
     * GA, Bloomreach, Attraqt event tracking etc.
     */
    removeItem(payload) {
      return new Promise((resolve, reject) => {
        let request = {}

        Cart.remove(payload.key)
          .then((response) => {
            request = {
              event: 'cart:removed',
              meta: JSON.parse(JSON.stringify(payload.meta) || '{}'),
            }

            const matchingLineItem = this.cartData.items.find((item) => {
              return item.key === payload.key
            })

            if (matchingLineItem) {
              request.data = JSON.parse(JSON.stringify(matchingLineItem) || '{}')
            }

            resolve(response)
          })
          .catch((error) => {
            request.event = 'cart:removeFailed'
            request.error = error.message

            reject(error)
          })
          .finally(() => {
            this.handleCartUpdate({ request })
          })
      })
    },

    /**
     * Change a given line item from the cart.
     * - Data should be driven from the line item key.
     * @param {Object} payload - Cart payload data.
     * @param {Object} payload.data - Cart JS 'change.js' payload.
     * @param {Object} payload.meta - Custom object to pass data through, useful for
     * GA, Bloomreach, Attraqt event tracking etc.
     */
    changeItem(payload) {
      return new Promise((resolve, reject) => {
        let request = {}

        Cart.changeItem(payload.data)
          .then((response) => {
            request = {
              event: 'cart:changed',
              meta: JSON.parse(JSON.stringify(payload.meta) || '{}'),
            }

            const matchingLineItem = this.cartData.items.find((item) => {
              return item.key === payload.data.key
            })

            if (matchingLineItem) {
              request.data = JSON.parse(JSON.stringify(matchingLineItem) || '{}')
            }

            resolve(response)
          })
          .catch((error) => {
            request.event = 'cart:changeFailed'
            request.error = error.message

            mint.Log('fatal', 'Failed to change item in the cart',
              error,
              request,
            )

            reject(error)
          })
          .finally(() => {
            this.handleCartUpdate({ request })
          })
      })
    },

    /**
     * Empty the cart entirely.
     */
    clearCart() {
      return new Promise((resolve, reject) => {
        let request = {}

        Cart.clear()
          .then((response) => {
            request = {
              event: 'cart:cleared',
              meta: {},
              preventDrawer: true,
              preventTracking: true,
            }

            resolve(response)
          })
          .catch((error) => {
            request.event = 'cart:clearFailed'
            request.error = error.message

            reject(error)
          })
          .finally(() => {
            this.handleCartUpdate({ request })
          })
      })
    },

    /**
     * Updates cart attributes and note.
     * @example:
     *  this.updateAttributes({
     *    attributes: 'Foo',
    *  })
     * @param {Object} payload - Action payload.
     * @param {Object} [payload.attributes] - Attributes to set.
     * @param {String} [payload.note] - Cart note to set.
     * @returns {Promise}
     */
    updateAttributes(payload) {
      return new Promise((resolve, reject) => {
        let request = {}

        Cart.updateAttributes(payload)
          .then((response) => {
            request = {
              data: payload,
              event: 'cart:updateAttributes',
              meta: JSON.parse(JSON.stringify(payload.meta) || '{}'),
              preventDrawer: true,
              preventTracking: true,
            }

            resolve(response)
          })
          .catch((error) => {
            request.event = 'cart:updateAttributesFailed'
            request.error = error.message

            reject(error)
          })
          .finally(() => {
            this.handleCartUpdate({ request })
          })
      })
    },

    /**
     * Query Shopify for the most up-to-date cart and append some
     * custom data to be used across the store.
     * @param {Object} data - Payload for 'handleCartUpdate'
     * @param {Object} data.request - Contains data for the request that triggered
     * a cart update. Typically:
     * - event: name of the cart event, 'cart:add', 'cart:remove' etc.
     * - event: name of the cart event, 'cart:add', 'cart:remove' etc.
     * - data: Payload of the initial cart request.
     */
    handleCartUpdate({
      request = {
        event: 'cart:fetch',
        preventTracking: true,
      },
    } = false) {
      Cart.fetchCart()
        .then((response) => {
          const newCart = {
            ...response,
            request,
          }

          /**
           * Store the cart int the state.
           */
          this.setCart(newCart)

          /**
           * Dispatch an event for ES6 components to use cart store.
           * - section-methods.js and core-methods.js listen to this event.
           * - Both of these will run this.setCart() (below) and keep Pinia in sync
           *   between all of the different Vue instances.
           * - Standard ES6 components can also listen to this event.
           */
          mint.EventBus.dispatch('mint:cart:updated', newCart)
        })
    },

    /**
     * Standalone method to update the cart in the global state
     * - This method is used in mint-sections.js to keep the Pinia
     *   state of each section is sync when the cart is added to.
     * - Response is added to browser cache, so when new section mounts
     *   it can add the data to that sections Pinia without doing
     *   another API call.
     * @param {Object} response
     */
    setCart(response) {
      if (!response) {
        return
      }

      this.cartData = response
      sessionStorage.setItem(storageNamespace.pinia.cart, JSON.stringify(response))
    },
  },
})